import { baseApi } from "../../api/baseApi";

const programApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ── Public Programs Page ──────────────────────────────────────────
    getPublicPrograms: builder.query({
      query: (options = {}) => {
        const params = new URLSearchParams();
        params.set('records', options.records || 9);
        params.set('page', options.page || 1);
        if (options.search) params.set('search', options.search);
        if (options.country) params.set('university__country__name__icontains', options.country);
        if (options.subject) params.set('subject__name__icontains', options.subject);
        if (options.type) params.set('type', options.type);
        if (options.tuition_max) params.set('tuition_fee__lte', options.tuition_max);
        if (options.ordering) params.set('ordering', options.ordering);
        return { url: `/education/public/programs/?${params.toString()}`, method: 'GET' };
      },
      serializeQueryArgs: ({ queryArgs }) =>
        JSON.stringify({
          page: queryArgs?.page || 1,
          records: queryArgs?.records || 9,
          search: queryArgs?.search || '',
          country: queryArgs?.country || '',
          subject: queryArgs?.subject || '',
          type: queryArgs?.type || '',
          tuition_max: queryArgs?.tuition_max || '',
          ordering: queryArgs?.ordering || '',
        }),
      providesTags: ['publicProgram'],
    }),
    getPublicProgramById: builder.query({
      query: (id) => ({ url: `/education/public/programs/${id}/`, method: 'GET' }),
      providesTags: ['publicProgram'],
    }),
    getPublicProgramBySlug: builder.query({
      query: (slug) => ({ url: `/education/public/programs/by-slug/${slug}/`, method: 'GET' }),
      providesTags: ['publicProgram'],
    }),
    getPublicCountries: builder.query({
      query: () => ({ url: '/education/public/countries/', method: 'GET' }),
    }),
    getPublicSubjects: builder.query({
      query: () => ({ url: '/education/public/subjects/', method: 'GET' }),
    }),
    // Faculty reference data. Sourced from the endpoint rather than derived
    // from the subject list so a faculty with no subjects yet is still pickable.
    getFaculties: builder.query({
      query: () => ({ url: '/education/public/faculties/', method: 'GET' }),
      providesTags: ["Faculties"],
    }),
    createFaculty: builder.mutation({
      query: (faculty) => ({
        url: `/education/faculty/`,
        method: "POST",
        body: faculty,
      }),
      // Subject rows carry faculty_name, so a rename must refresh them too.
      invalidatesTags: ["Faculties", "Subjects"],
    }),
    updateFaculty: builder.mutation({
      query: ({ id, facultyData }) => ({
        url: `/education/faculty/${id}/`,
        method: "PATCH",
        body: facultyData,
      }),
      invalidatesTags: ["Faculties", "Subjects"],
    }),
    deleteFaculty: builder.mutation({
      query: ({ id }) => ({
        url: `/education/faculty/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["Faculties", "Subjects"],
    }),
    getPublicUniversities: builder.query({
      query: ({ search = '', country = '' } = {}) => {
        const params = new URLSearchParams();
        if (search) params.set('search', search);
        if (country) params.set('country', country);
        return { url: `/education/public/universities/?${params.toString()}`, method: 'GET' };
      },
    }),
    // ── End Public Programs Page ──────────────────────────────────────

    ieltsLanguage: builder.query({
      query: (language) => `/education/language/${language}/`,
    }),
    getSubjects: builder.query({
      query: () => ({
        url: `/admin/subject/?records=1000`,
        method: "GET",
      }),
      providesTags: ["Subjects"],
    }),
    getSubjectByID: builder.query({
      query: (id) => ({
        url: `/admin/subject/${id}/`,
        method: "GET",
      }),
      providesTags: ["Subjects"],
    }),
    getAllLanguage: builder.query({
      query: (language) => ({
        url: `/education/language/${language}/`,
        method: "GET",
      }),
      providesTags: ["language"],
    }),

    getLanguage: builder.query({
      query: ({ language, id }) => ({
        url: `/education/language/${language}/${id}/`,
        method: "GET",
      }),
      providesTags: ["language"],
    }),

    createSubject: builder.mutation({
      query: (subject) => {
        return {
          url: `/education/subject/`,
          method: "POST",
          body: subject,
        };
      },
      invalidatesTags: ["Subjects"],
    }),
    updateSubject: builder.mutation({
      query: ({ id, subjectData }) => {
        return {
          url: `/education/subject/${id}/`,
          method: "PATCH",
          body: subjectData,
        };
      },
      invalidatesTags: ["Subjects"],
    }),

    deleteSubject: builder.mutation({
      query: ({ id }) => ({
        url: `/education/subject/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["Subjects"],
    }),

    createLanguage: builder.mutation({
      query: ({ type, language }) => ({
        url: `/education/language/${type}/`,
        method: "POST",
        body: language,
      }),
      invalidatesTags: ["language"],
    }),

    updateLanguage: builder.mutation({
      query: ({ type, language, id }) => ({
        url: `/education/language/${type}/${id}/`,
        method: "PATCH",
        body: language,
      }),
      invalidatesTags: ["language"],
    }),

    deleteLanguage: builder.mutation({
      query: ({ type, id }) => ({
        url: `/education/language/${type}/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["language"],
    }),

    university: builder.query({
      query: (options) => {
        const params = new URLSearchParams({ records: 500 });
        if (options?.university_name) params.set("name__icontains", options.university_name);
        if (options?.country) params.set("country", options.country);
        return { url: `/education/university/?${params.toString()}`, method: "GET" };
      },
    }),

    getUniversityById: builder.query({
      query: (id) => ({
        url: `/education/university/${id}/`,
        method: "GET",
      }),
      providesTags: ["university"],
    }),

    getUniversityBySlug: builder.query({
      query: (slug) => ({
        url: `/education/university/detail/${slug}/`,
        method: "GET",
      }),
      providesTags: ["university"],
    }),

    updateUniversityById: builder.mutation({
      query: ({ id, updatedData }) => {
        const formData = new FormData();
        Object.entries(updatedData).forEach(([key, val]) => {
          if (val !== undefined && val !== null) formData.append(key, val);
        });
        return { url: `/education/university/${id}/`, method: "PATCH", body: formData };
      },
      invalidatesTags: ["university"],
    }),

    createUniversity: builder.mutation({
      query: (uniData) => {
        const formData = new FormData();
        Object.entries(uniData).forEach(([key, val]) => {
          if (val === undefined || val === null) return;
          // The optional nested agreement is an object — JSON-encode it so it
          // survives multipart FormData and the backend can create both atomically.
          if (key === "agreement" && typeof val === "object") {
            formData.append(key, JSON.stringify(val));
          } else {
            formData.append(key, val);
          }
        });
        return { url: "/education/university/", method: "POST", body: formData };
      },
      invalidatesTags: ["university", "Agreement"],
    }),

    deleteUniversity: builder.mutation({
      query: (id) => ({
        url: `/education/university/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["university"],
    }),

    addProgram: builder.mutation({
      query: (programData) => ({
        url: "/education/program/",
        method: "POST",
        body: programData,
      }),
    }),
    updateProgramById: builder.mutation({
      query: ({ id, updatedData }) => ({
        url: `/education/program/${id}/`,
        method: "PATCH",
        body: updatedData,
      }),
      // publicProgram too: activating/deactivating changes the public site.
      invalidatesTags: ["program", "publicProgram"],
    }),
    getProgramByid: builder.query({
      query: (id) => ({
        url: `/education/program/${id}/`,
        method: "GET",
      }),
    }),
    allProgram: builder.query({
      query: (options) => {
        // `light=1` returns a slim payload (only the columns the table renders),
        // see ProgramListSerializer on the backend. Roughly 10x smaller and
        // avoids N+1 queries on SSC/HSC/Bachelor/experience.
        let url = `/education/program/?light=1&records=${options?.records || 40}&page=${options?.page || 1}&`;

        const params = [];

        if (options?.university)
          params.push(`university__name__icontains=${options.university}`);

        // Exact branch match — same-name universities exist at different
        // locations, so name-contains would mix their programs together.
        if (options?.university_id)
          params.push(`university=${options.university_id}`);

        // Several branches at once (e.g. every campus of one university).
        if (options?.university_ids?.length)
          params.push(`university__in=${options.university_ids.join(",")}`);

        if (options?.program) params.push(`type=${options.program}`);

        // Status filter: "active" / "inactive" (blank = all).
        if (options?.status === "active") params.push("is_active=true");
        if (options?.status === "inactive") params.push("is_active=false");

        if (options?.subject)
          params.push(`subject__name__icontains=${options.subject}`);

        if (options?.country)
          params.push(`university__country__name__icontains=${options.country}`);

        // The picked score is the applicant's, so match every program asking
        // for that or less. Programs with no requirement for that test are
        // kept by the backend filter — nothing to fail means still eligible.
        if (options?.language) {
          const [baseLanguage, score] = options.language.split("-");
          if (["ielts", "pte", "toefl", "duolingo"].includes(baseLanguage)) {
            params.push(`${baseLanguage}_max=${score}`);
          }
        }

        // Applicant's own 10th/12th grade marks — the backend checks the SSC
        // and HSC requirements together and keeps programs that state neither.
        if (options?.math) params.push(`math_max=${options.math}`);

        if (options?.physics) params.push(`physics_max=${options.physics}`);

        // The entered figure is the applicant's ceiling — return everything at
        // or under it, plus programs with no fee on record.
        if (options?.tution) params.push(`tuition_max=${options.tution}`);

        if (params.length) url += params.join("&");

        return { url, method: "GET" };
      },

      // 🔑 THIS IS THE FIX
      serializeQueryArgs: ({ queryArgs }) => {
        return JSON.stringify({
          page: queryArgs?.page || 1,
          program: queryArgs?.program || "",
          country: queryArgs?.country || "",
          subject: queryArgs?.subject || "",
          language: queryArgs?.language || "",
          math: queryArgs?.math || "",
          physics: queryArgs?.physics || "",
          tution: queryArgs?.tution || "",
          university: queryArgs?.university || "",
          university_id: queryArgs?.university_id || "",
          university_ids: queryArgs?.university_ids || [],
          records: queryArgs?.records || 40,
          status: queryArgs?.status || "",
        });
      },

      keepUnusedDataFor: 300,
      providesTags: ["program"],
    }),

    // Activate / deactivate only — its own endpoint so nothing else on the
    // program is touched (a partial PATCH goes through the full edit form).
    setProgramActive: builder.mutation({
      query: ({ id, isActive }) => ({
        url: `/education/program/${id}/set-active/`,
        method: "POST",
        body: { is_active: isActive },
      }),
      invalidatesTags: ["program", "publicProgram"],
    }),

    deleteProgramById: builder.mutation({
      query: (id) => ({
        url: `/education/program/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["program", "publicProgram"],
    }),

    programWithID: builder.query({
      query: (id) => ({
        url: `/education/program/${id}/`,
        method: "GET",
      }),
      providesTags: ["program"],
    }),

    createStudentApplication: builder.mutation({
      query: (applicant) => ({
        url: "/education/applicant/profile/",
        method: "POST",
        body: applicant,
      }),
    }),
    createStudentByPartner: builder.mutation({
      query: (studentData) => ({
        url: "/admin/agent/student/",
        method: "POST",
        body: studentData,
      }),
      invalidatesTags: ["student"],
    }),
    getAllStudentByPartner: builder.query({
      query: (options) => {
        let url = `/admin/agent/student/?records=40&page=${
          options?.page || 1
        }&`;

        const params = [];

        if (options?.first_name) params.push(`first_name=${options.first_name}`);
        if (options?.study_country) params.push(`study_country=${options.study_country}`);

        if (params.length) url += params.join("&");

        return { url, method: "GET" };
      },

      serializeQueryArgs: ({ queryArgs }) => {
        return JSON.stringify({
          page: queryArgs?.page || 1,
          first_name: queryArgs?.first_name || "",
          study_country: queryArgs?.study_country || "",
        });
      },

      keepUnusedDataFor: 300,
      providesTags: ["student"],
    }),
    getAllAlpplicantList: builder.query({
      query: () => ({
        url: "/admin/applicant/list/",
        method: "GET",
      }),
    }),
    getApplicantDetailsById: builder.query({
      query: (id) => ({
        url: `/education/applicant/profile/${id}/`,
        method: "GET",
      }),
      providesTags: ["applicant"],
    }),
    createExperience: builder.mutation({
      query: (experience) => ({
        url: "/admin/work_experience/",
        method: "POST",
        body: experience,
      }),
      invalidatesTags: ["experience"],
    }),
    updateExperience: builder.mutation({
      query: ({ id, experienceData }) => {
        return {
          url: `/admin/work_experience/${id}/`,
          method: "PATCH",
          body: experienceData,
        };
      },
      invalidatesTags: ["experience"],
    }),
    getExperience: builder.query({
      query: () => ({
        url: "/admin/work_experience/?records=500",
        method: "GET",
      }),
      providesTags: ["experience"],
    }),
    getExperienceByID: builder.query({
      query: (id) => ({
        url: `/admin/work_experience/${id}/`,
        method: "GET",
      }),
      providesTags: ["experience"],
    }),
    deleteExperience: builder.mutation({
      query: ({ id }) => ({
        url: `/admin/work_experience/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["Experience"],
    }),

    checkEligibility: builder.query({
      query: (id) => ({
        url: id
          ? `/education/eligibility/?student=${id}&records=500`
          : "/education/eligibility/?records=500",
        method: "GET",
      }),
      providesTags: ["eligibility"],
    }),

    /* Runs the saved-profile test. For a student's own profile this uses one
       of their limited runs; agency/staff pass `student` and are not limited.
       The GET above then serves the new result. */
    runEligibility: builder.mutation({
      query: (studentId) => ({
        url: "/education/eligibility/run/",
        method: "POST",
        body: studentId ? { student: studentId } : {},
      }),
      invalidatesTags: ["eligibility", "eligibilityStats"],
    }),

    /* A student's checks left and last-run date — no programs, no matching.
       What the student's eligibility page loads on open. */
    getEligibilityStatus: builder.query({
      query: () => ({ url: "/education/eligibility/status/", method: "GET" }),
      providesTags: ["eligibility"],
    }),

    /* Run counts: everything for UniEdu staff, own numbers for an agency. */
    getEligibilityStats: builder.query({
      query: () => ({ url: "/education/eligibility/stats/", method: "GET" }),
      providesTags: ["eligibilityStats"],
    }),

    /* Standalone eligibility check — runs the same matching rules against
       academic data posted in the body instead of a saved Student profile.
       No profile is touched; the run is only logged for the stats. */
    quickEligibility: builder.mutation({
      query: (payload) => ({
        url: "/education/eligibility/quick/",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["eligibilityStats"],
    }),

    /* Same check, open to anonymous visitors (public /eligibility page).
       Returns a reduced program shape and is rate-limited server-side. */
    publicEligibility: builder.mutation({
      query: (payload) => ({
        url: "/education/public/eligibility/",
        method: "POST",
        body: payload,
      }),
    }),
    getAdminUserData: builder.query({
      query: (options) => {
        if (options?.referBy) {
          return {
            url: `/admin/userdata/list/?records=1000&refer_by=${options.referBy}`,
            method: "GET",
          };
        }
        return {
          url: `/admin/userdata/list/?records=1000`,
          method: "GET",
        };
      },
      providesTags: ["AdminUserData"],
    }),
    getAdminUserDataById: builder.query({
      query: (id) => ({
        url: `/admin/userdata/details/${id}/`,
        method: "GET",
      }),
    }),
    getAdminApplicantDataById: builder.query({
      query: (id) => ({
        url: `/admin/applicant/details/${id}/?records=1000`,
        method: "GET",
      }),
      providesTags: ["applicant"],
    }),
    getAllComments: builder.query({
      query: (studentCode) => {
        const url = studentCode
          ? `/education/comment-on-student/?student__code=${studentCode}&records=500`
          : "/education/comment-on-student/?records=500";
        return { url, method: "GET" };
      },
      providesTags: ["comment"],
    }),

    /* ── Private notes (owner-only, no polling) ── */
    getStudentNotes: builder.query({
      query: (studentCode) => ({
        url: `/education/student-notes/?student__code=${studentCode}&records=500`,
        method: "GET",
      }),
      providesTags: ["studentNote"],
    }),
    // { students: { "<code>": { last_note_at, next_contact_at } } } — powers
    // the follow-up column on the student list.
    getStudentNoteFollowUp: builder.query({
      query: () => ({
        url: `/education/student-notes/follow-up/`,
        method: "GET",
      }),
      providesTags: ["studentNote"],
    }),
    createStudentNote: builder.mutation({
      query: (body) => ({
        url: `/education/student-notes/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["studentNote"],
    }),
    updateStudentNote: builder.mutation({
      query: ({ id, body }) => ({
        url: `/education/student-notes/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["studentNote"],
    }),
    deleteStudentNote: builder.mutation({
      query: (id) => ({
        url: `/education/student-notes/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["studentNote"],
    }),
    getAllCommentsByID: builder.query({
      query: (id) => ({
        url: `/education/comment-on-student/${id}/`,
        method: "GET",
      }),
      providesTags: ["comment"],
    }),
    createNewComments: builder.mutation({
      query: (comments) => ({
        url: "/education/comment-on-student/",
        method: "POST",
        body: comments,
      }),
      invalidatesTags: ["comment"],
    }),
    deleteComments: builder.mutation({
      query: (id) => ({
        url: `/education/comment-on-student/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["comment"],
    }),
    // Student-facing conversation: read admin notes on own profile + reply
    getMyComments: builder.query({
      query: () => ({
        url: "/education/my-comments/?records=500",
        method: "GET",
      }),
      providesTags: ["comment"],
    }),
    addMyComment: builder.mutation({
      query: (body) => ({
        url: "/education/my-comments/",
        method: "POST",
        body,
      }),
      invalidatesTags: ["comment"],
    }),
    // Unread badges: student's own count + admin per-student map
    getMyUnreadCount: builder.query({
      query: () => ({
        url: "/education/my-comments/unread/",
        method: "GET",
      }),
      providesTags: ["comment"],
    }),
    getCommentUnreadMap: builder.query({
      query: () => ({
        url: "/education/comment-unread/",
        method: "GET",
      }),
      providesTags: ["comment"],
    }),

    getAgentStudentById: builder.query({
      query: (decodedId) => ({
        url: `/admin/student/detail/${decodedId}/`,
        method: "GET",
      }),
      providesTags: ["Agent"],
    }),

    updateAgentStudentById: builder.mutation({
      query: ({ id, data }) => ({
        url: `/admin/student/update/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Agent", "student"],
    }),
  }),
});

export const {
  useGetPublicProgramsQuery,
  useGetPublicProgramByIdQuery,
  useGetPublicProgramBySlugQuery,
  useGetPublicCountriesQuery,
  useGetPublicSubjectsQuery,
  useGetFacultiesQuery,
  useCreateFacultyMutation,
  useUpdateFacultyMutation,
  useDeleteFacultyMutation,
  useGetPublicUniversitiesQuery,
  useIeltsLanguageQuery,
  useGetSubjectsQuery,
  useGetSubjectByIDQuery,
  useUpdateSubjectMutation,
  useDeleteSubjectMutation,
  useGetAllLanguageQuery,
  useGetUniversityByIdQuery,
  useGetUniversityBySlugQuery,
  useUpdateUniversityByIdMutation,
  useGetLanguageQuery,
  useDeleteLanguageMutation,
  useUniversityQuery,
  useCreateSubjectMutation,
  useCreateLanguageMutation,
  useUpdateLanguageMutation,
  useCreateUniversityMutation,
  useDeleteUniversityMutation,
  useAddProgramMutation,
  useUpdateProgramByIdMutation,
  useAllProgramQuery,
  useGetProgramByidQuery,
  useProgramWithIDQuery,
  useDeleteProgramByIdMutation,
  useSetProgramActiveMutation,
  useCreateStudentApplicationMutation,
  useGetAllAlpplicantListQuery,
  useCheckEligibilityQuery,
  useRunEligibilityMutation,
  useGetEligibilityStatusQuery,
  useGetEligibilityStatsQuery,
  useQuickEligibilityMutation,
  usePublicEligibilityMutation,
  // useCheckEligibilityAdminQuery,
  useGetExperienceQuery,
  useGetExperienceByIDQuery,
  useCreateExperienceMutation,
  useUpdateExperienceMutation,
  useDeleteExperienceMutation,
  useGetAdminUserDataQuery,
  useGetAdminUserDataByIdQuery,
  useGetAdminApplicantDataByIdQuery,
  useGetApplicantDetailsByIdQuery,
  useCreateNewCommentsMutation,
  useGetAllCommentsQuery,
  useGetStudentNotesQuery,
  useGetStudentNoteFollowUpQuery,
  useCreateStudentNoteMutation,
  useUpdateStudentNoteMutation,
  useDeleteStudentNoteMutation,
  useGetAllCommentsByIDQuery,
  useDeleteCommentsMutation,
  useGetMyCommentsQuery,
  useAddMyCommentMutation,
  useGetMyUnreadCountQuery,
  useGetCommentUnreadMapQuery,
  useCreateStudentByPartnerMutation,
  useGetAllStudentByPartnerQuery,
  useGetAgentStudentByIdQuery,
  useUpdateAgentStudentByIdMutation,
} = programApi;
